import { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { MdArrowBack, MdSearch } from 'react-icons/md'
import {
  useGetHelpArticleQuery,
  useGetHelpArticlesQuery,
  useSearchHelpQuery,
} from '../../api/helpApiSlice'
import { getHelpPlatform } from '../../utils/helpPlatform'
import HelpText from './HelpText'
import styles from './Help.module.scss'

const MIN_QUERY_LENGTH = 2
const SEARCH_DELAY_MS = 300

const useDebounced = (value, delay) => {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay)
    return () => clearTimeout(timer)
  }, [value, delay])
  return debounced
}

const ArticleList = ({ articles, onOpen }) => (
  <ul className={styles.list}>
    {articles.map((article) => (
      <li key={article.slug}>
        <button type='button' className={styles.item} onClick={() => onOpen(article.slug)}>
          <span className={styles.itemTitle}>{article.title}</span>
          {article.summary ? <span className={styles.itemSummary}>{article.summary}</span> : null}
        </button>
      </li>
    ))}
  </ul>
)

ArticleList.propTypes = { articles: PropTypes.array, onOpen: PropTypes.func }

const Article = ({ slug, onBack }) => {
  const { data, isLoading, isError } = useGetHelpArticleQuery(slug)
  return (
    <div>
      <button type='button' className={styles.back} onClick={onBack}>
        <MdArrowBack /> Back
      </button>
      {isLoading ? <p className={styles.note}>Loading...</p> : null}
      {isError ? <p className={styles.note}>This article is not available.</p> : null}
      {data?.article ? (
        <article className={styles.article}>
          <h2>{data.article.title}</h2>
          <HelpText text={data.article.body} />
        </article>
      ) : null}
    </div>
  )
}

Article.propTypes = { slug: PropTypes.string, onBack: PropTypes.func }

/**
 * Help with search, a list of topics and the text of a topic.
 * context: screen key, shows the topics for that screen first (a link shows all of them)
 */
const HelpPanel = ({ platform, context, slug, onSlugChange }) => {
  const helpPlatform = getHelpPlatform(platform)
  const [query, setQuery] = useState('')
  const [showAll, setShowAll] = useState(!context)
  const [openSlug, setOpenSlug] = useState(slug || null)
  const debouncedQuery = useDebounced(query.trim(), SEARCH_DELAY_MS)
  const searching = debouncedQuery.length >= MIN_QUERY_LENGTH

  useEffect(() => setOpenSlug(slug || null), [slug])

  const openArticle = (value) => {
    setOpenSlug(value)
    if (onSlugChange) onSlugChange(value)
  }

  const list = useGetHelpArticlesQuery({
    platform: helpPlatform,
    context: showAll ? undefined : context,
  })
  const search = useSearchHelpQuery(
    { q: debouncedQuery, platform: helpPlatform },
    { skip: !searching },
  )

  // A screen without its own topics falls back to all topics
  useEffect(() => {
    if (!showAll && list.data && !list.data.articles?.length) setShowAll(true)
  }, [showAll, list.data])

  if (openSlug) return <Article slug={openSlug} onBack={() => openArticle(null)} />

  const shown = searching ? search : list
  const unavailable = shown.isError

  return (
    <div className={styles.panel}>
      <div className={styles.searchWrapper}>
        <MdSearch className={styles.searchIcon} />
        <input
          type='search'
          className={styles.searchInput}
          placeholder='Search help'
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          autoFocus
        />
      </div>
      {unavailable ? <p className={styles.note}>Help is not available right now.</p> : null}
      {!unavailable && shown.isFetching ? <p className={styles.note}>Loading...</p> : null}
      {!unavailable && !shown.isFetching && shown.data && !shown.data.articles?.length ? (
        <p className={styles.note}>
          {searching ? 'Nothing found. Try a different word.' : 'No topics yet.'}
        </p>
      ) : null}
      {!unavailable && shown.data?.articles?.length ? (
        <ArticleList articles={shown.data.articles} onOpen={openArticle} />
      ) : null}
      {!searching && !showAll ? (
        <button type='button' className={styles.showAll} onClick={() => setShowAll(true)}>
          Show all topics
        </button>
      ) : null}
    </div>
  )
}

HelpPanel.propTypes = {
  platform: PropTypes.string,
  context: PropTypes.string,
  slug: PropTypes.string,
  onSlugChange: PropTypes.func,
}

export default HelpPanel
